import React, { useEffect, useCallback } from 'react';
import { VideoPlayer } from 'expo-video';
import { VideoPlayerCore } from '@/student/task/components/VideoPlayer/core/VideoPlayerCore';
import { VideoPlayerProps } from '@/student/task/components/VideoPlayer/core/types';
import { useVideoResourceInit } from '@/student/task/components/VideoPlayer/store/videoPlayerStore';
import log from '@/common/utils/logger';

/**
 * VideoPlayerWithProgress is a wrapper around VideoPlayerCore that adds progress tracking.
 * It manages restoring progress when the video is loaded
 * and saving progress when appropriate (play/pause, modal close).
 */
export const VideoPlayerWithProgress = React.forwardRef<VideoPlayer, VideoPlayerProps>(
  ({ resource }, ref) => {
    const videoRef = React.useRef<VideoPlayer>(null);
    const hasResumedRef = React.useRef(false);

    // Initialize store with the current resource
    const { initializeResource } = useVideoResourceInit();

    // Combine external and internal refs
    React.useImperativeHandle(ref, () => videoRef.current!, []);

    // Handle video ready state
    /**
     * Plays the video and suppresses benign AbortError rejections.
     */
    const playWithHandling = useCallback((player: VideoPlayer) => {
      if (!player) return;

      const handlePlayError = (error: unknown) => {
        const typedError = error as { name?: string } | undefined;
        if (typedError?.name === 'AbortError') {
          log.debug('Video playback aborted by concurrent call');
          return;
        }
        log.error('Video playback error:', error);
      };

      try {
        const playResult = player.play() as Promise<void> | void;
        if (playResult && typeof playResult.catch === 'function') {
          playResult.catch(handlePlayError);
        }
      } catch (error) {
        handlePlayError(error);
      }
    }, []);

    // Initialize resource when component mounts
    useEffect(() => {
      initializeResource(resource?.id);
    }, [resource, initializeResource]);

    useEffect(() => {
      hasResumedRef.current = false;
    }, [resource?.id]);

    const videoProgress = resource?.progress?.progress as { watchedSeconds?: number } | undefined;
    const watchedSeconds = videoProgress?.watchedSeconds || 0;
    const shouldResume = watchedSeconds > 0;

    const handleReadyForDisplay = useCallback((player: VideoPlayer) => {
      if (!shouldResume || hasResumedRef.current) return;
      hasResumedRef.current = true;

      // expo-video uses seconds, not milliseconds. Setting currentTime seeks the player.
      const playerInstance = player;

      // Only seek if we're not already there (prevents unnecessary interruptions)
      if (Math.abs(playerInstance.currentTime - watchedSeconds) > 0.5) {
        playerInstance.currentTime = watchedSeconds;
      }

      // If pauseOnReady was true, VideoPlayerCore already called player.pause().
      // If autoPlay was true, we might be playing.
      // We don't call pause() again here to avoid interrupting a pending play() promise.
      playWithHandling(playerInstance);
    }, [shouldResume, watchedSeconds, playWithHandling]);

    const handleError = useCallback((error: string) => {
      // Log with resource info to help identify corrupt/invalid video files
      log.warn('Video playback failed - possible corrupt or invalid file', {
        resourceId: resource?.id,
        url: resource?.url?.substring(0, 100), // Truncate URL for privacy
        error,
      });
    }, [resource?.id, resource?.url]);

    return (
      <VideoPlayerCore
        ref={videoRef}
        accessUrl={resource.url}
        onError={handleError}
        onReadyForDisplay={handleReadyForDisplay}
        autoPlay={!shouldResume}
        pauseOnReady={shouldResume}
      />
    );
  },
);
